import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { 
  FileText, Package, TrendingUp, TrendingDown, Download, Filter, 
  Calendar, Search, RefreshCw, ChevronDown, X, Receipt, ShoppingBag, Users, DollarSign
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { setupMultilingualPDF, autoTableMultilingualHook } from '../../utils/pdfMultilingual';
import { hasIndicScript, exportTableReportPdf } from '../../utils/pdfHtmlRender';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/pos`;

// Searchable Customer Select Component
const SearchableCustomerSelect = ({ customers, value, onChange, placeholder = 'All Customers', testId = 'customer-select' }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const btnRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        // Also check if click is inside the fixed dropdown
        const dropdown = document.querySelector(`[data-testid="${testId}-dropdown"]`);
        if (dropdown && dropdown.contains(e.target)) return;
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [testId]);

  const handleOpen = () => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    setOpen(!open);
  };

  const filtered = customers.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.name || '').toLowerCase().includes(q) || (c.mobile || c.phone || '').includes(q);
  });

  const selected = customers.find(c => c.id === value);
  const displayText = selected ? `${selected.name} - ${selected.mobile || selected.phone || ''}` : placeholder;

  return (
    <div ref={ref} className="relative" data-testid={testId}>
      <button
        ref={btnRef}
        type="button"
        onClick={handleOpen}
        className={`w-full flex items-center justify-between px-3 py-2 border border-slate-200 rounded-lg text-sm text-left focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white ${value ? 'text-slate-800' : 'text-slate-400'}`}
      >
        <span className="truncate">{displayText}</span>
        <ChevronDown size={14} className={`ml-1 flex-shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          className="bg-white border border-slate-200 rounded-lg shadow-lg"
          data-testid={`${testId}-dropdown`}
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 9999, maxHeight: '280px' }}
        >
          <div className="p-2 border-b border-slate-100 sticky top-0 bg-white z-10">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name or mobile..."
                className="w-full pl-8 pr-3 py-1.5 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-violet-500"
                autoFocus
                data-testid={`${testId}-search`}
              />
            </div>
          </div>
          <div className="overflow-y-auto" style={{ maxHeight: '230px' }}>
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false); setSearch(''); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-violet-50 ${!value ? 'bg-violet-50 text-violet-700 font-medium' : 'text-slate-600'}`}
            >
              {placeholder}
            </button>
            {filtered.map(c => (
              <button
                key={c.id}
                type="button"
                onClick={() => { onChange(c.id); setOpen(false); setSearch(''); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-violet-50 ${value === c.id ? 'bg-violet-50 text-violet-700 font-medium' : 'text-slate-800'}`}
              >
                <span className="font-medium">{c.name}</span>
                {(c.mobile || c.phone) && <span className="text-slate-400 ml-1.5">- {c.mobile || c.phone}</span>}
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="px-3 py-3 text-sm text-slate-400 text-center">No customers found</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper to format date for display
const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Get today's date in YYYY-MM-DD format
const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// Tab Component
const TabButton = ({ active, onClick, icon: Icon, label }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-3 font-medium transition-all border-b-2 ${
      active 
        ? 'text-violet-600 border-violet-600 bg-violet-50' 
        : 'text-slate-500 border-transparent hover:text-violet-600 hover:bg-slate-50'
    }`}
  >
    <Icon size={18} />
    <span className="hidden sm:inline">{label}</span>
  </button>
);

// Sales Report Component
const SalesReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(getTodayDate());
  const [toDate, setToDate] = useState(getTodayDate());
  const [paymentMode, setPaymentMode] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [customers, setCustomers] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchCustomers();
    fetchReport();
  }, []);

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('pos_token');
      const params = { from_date: fromDate, to_date: toDate };
      if (paymentMode) params.payment_mode = paymentMode;
      if (customerId) params.customer_id = customerId;

      const response = await axios.get(`${API}/reports/sales-detailed`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!data?.data?.length) return;
    const headers = ['Sl.No', 'Date & Time', 'Customer Name', 'Mobile', 'Bill No', 'Total', 'Discount', 'Net', 'Payment Mode', 'Status'];
    const rows = data.data.map(r => [
      r.sl_no, formatDateTime(r.date_time), r.customer_name, r.customer_mobile,
      r.bill_number, r.total_amount.toFixed(2), r.discount.toFixed(2), r.net_amount.toFixed(2),
      r.payment_mode, r.payment_status
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales_report_${fromDate}_to_${toDate}.csv`;
    a.click();
  };

  const exportToPDF = async () => {
    if (!data?.data?.length) return;
    const tableBody = data.data.map(r => [
      r.sl_no, formatDateTime(r.date_time), r.customer_name, r.bill_number,
      r.total_amount.toFixed(2), r.discount.toFixed(2), r.net_amount.toFixed(2), r.payment_mode, r.payment_status
    ]);
    const metaLines = [
      `Period: ${fromDate} to ${toDate}`,
      `Total Bills: ${data.summary?.total_bills || data.data.length} | Gross: Rs.${data.summary?.gross_amount?.toFixed(2) || '0'} | Net: Rs.${data.summary?.net_amount?.toFixed(2) || '0'}`
    ];
    const head = ['Sl', 'Date & Time', 'Customer', 'Bill No', 'Total', 'Discount', 'Net', 'Pay Mode', 'Status'];
    if (hasIndicScript(data.data.flatMap(r => [r.customer_name, r.bill_number]).join(' '))) {
      await exportTableReportPdf({
        title: 'Sales Report', metaLines, orientation: 'l',
        filename: `Sales_Report_${fromDate}_to_${toDate}.pdf`,
        sections: [{ head, body: tableBody, align: ['c', '', '', '', 'r', 'r', 'r', '', ''] }]
      });
      return;
    }
    const pdf = new jsPDF('l', 'mm', 'a4');
    await setupMultilingualPDF(pdf, data.data.flatMap(r => [r.customer_name, r.bill_number]));
    pdf.setFontSize(14); pdf.setFont('helvetica', 'bold');
    pdf.text('Sales Report', 14, 15);
    pdf.setFontSize(9); pdf.setFont('helvetica', 'normal');
    pdf.text(`Period: ${fromDate} to ${toDate}`, 14, 22);
    pdf.text(`Total Bills: ${data.summary?.total_bills || data.data.length} | Gross: Rs.${data.summary?.gross_amount?.toFixed(2) || '0'} | Net: Rs.${data.summary?.net_amount?.toFixed(2) || '0'}`, 14, 28);
    autoTable(pdf, {
      startY: 33,
      head: [['Sl', 'Date & Time', 'Customer', 'Bill No', 'Total', 'Discount', 'Net', 'Pay Mode', 'Status']],
      body: tableBody,
      theme: 'grid', headStyles: { fillColor: [79, 70, 229], fontSize: 8 }, bodyStyles: { fontSize: 7.5 },
      margin: { left: 14, right: 14 },
      didParseCell: autoTableMultilingualHook
    });
    pdf.save(`Sales_Report_${fromDate}_to_${toDate}.pdf`);
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-slate-400" />
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
            <span className="text-slate-400">to</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${
              showFilters ? 'bg-violet-50 border-violet-200 text-violet-600' : 'border-slate-200 text-slate-600'
            }`}
          >
            <Filter size={16} />
            Filters
            {(paymentMode || customerId) && <span className="w-2 h-2 bg-violet-600 rounded-full"></span>}
          </button>

          <button
            onClick={fetchReport}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 disabled:opacity-50"
          >
            {loading ? <RefreshCw size={16} className="animate-spin" /> : <Search size={16} />}
            Generate
          </button>

          {data?.data?.length > 0 && (
            <>
              <button onClick={exportToPDF} className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700" data-testid="sales-export-pdf"><Download size={16} /> PDF</button>
              <button onClick={exportToCSV} className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50"><Download size={16} /> CSV</button>
            </>
          )}
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Payment Mode</label>
              <select
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                <option value="">All Modes</option>
                <option value="cash">Cash</option>
                <option value="upi">UPI</option>
                <option value="card">Card</option>
                <option value="credit">Credit</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Customer</label>
              <SearchableCustomerSelect
                customers={customers}
                value={customerId}
                onChange={(val) => setCustomerId(val)}
                placeholder="All Customers"
                testId="sales-report-customer"
              />
            </div>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      {data?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Total Bills</p>
            <p className="text-2xl font-bold text-slate-800">{data.summary.total_bills}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Gross Amount</p>
            <p className="text-2xl font-bold text-slate-800">₹{data.summary.total_amount?.toLocaleString('en-IN')}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Total Discount</p>
            <p className="text-2xl font-bold text-orange-600">₹{data.summary.total_discount?.toLocaleString('en-IN')}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Net Amount</p>
            <p className="text-2xl font-bold text-emerald-600">₹{data.summary.total_net?.toLocaleString('en-IN')}</p>
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Sl.No</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Date & Time</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Customer</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Bill No</th>
                <th className="text-right px-4 py-3 font-semibold text-slate-700">Total</th>
                <th className="text-right px-4 py-3 font-semibold text-slate-700">Discount</th>
                <th className="text-right px-4 py-3 font-semibold text-slate-700">Net</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Payment</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="text-center py-8">
                    <RefreshCw className="animate-spin mx-auto text-violet-600" size={24} />
                  </td>
                </tr>
              ) : data?.data?.length > 0 ? (
                data.data.map((row) => (
                  <tr key={row.sl_no} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-600">{row.sl_no}</td>
                    <td className="px-4 py-3 text-slate-800">{formatDateTime(row.date_time)}</td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-800">{row.customer_name}</p>
                      <p className="text-xs text-slate-500">{row.customer_mobile}</p>
                    </td>
                    <td className="px-4 py-3 font-mono text-slate-700">{row.bill_number}</td>
                    <td className="px-4 py-3 text-right text-slate-800">₹{row.total_amount?.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right text-orange-600">₹{row.discount?.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-emerald-600">₹{row.net_amount?.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 capitalize">
                        {row.payment_mode}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        row.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                        row.payment_status === 'credit' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {row.payment_status}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} className="text-center py-8 text-slate-400">
                    No data found for the selected filters
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Stock In Report Component
const StockInReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(getTodayDate());
  const [toDate, setToDate] = useState(getTodayDate());
  const [supplierId, setSupplierId] = useState('');
  const [productId, setProductId] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [batchNumber, setBatchNumber] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchMasterData();
    fetchReport();
  }, []);

  const fetchMasterData = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const [suppRes, prodRes] = await Promise.all([
        axios.get(`${API}/suppliers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/products/enhanced`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setSuppliers(suppRes.data);
      setProducts(prodRes.data);
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('pos_token');
      const params = { from_date: fromDate, to_date: toDate };
      if (supplierId) params.supplier_id = supplierId;
      if (productId) params.product_id = productId;
      if (invoiceNumber) params.invoice_number = invoiceNumber;
      if (batchNumber) params.batch_number = batchNumber;

      const response = await axios.get(`${API}/reports/stock-in`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!data?.data?.length) return;
    const headers = ['Sl.No', 'Date & Time', 'Supplier', 'Mobile', 'Invoice No', 'Product', 'Qty', 'Unit', 'Cost', 'MRP', 'Discount', 'Batch', 'Expiry'];
    const rows = data.data.map(r => [
      r.sl_no, formatDateTime(r.date_time), r.supplier_name, r.supplier_mobile,
      r.invoice_number, r.product_name, r.stock_in, r.unit, r.cost_price.toFixed(2),
      r.mrp.toFixed(2), r.discount, r.batch_number, r.expiry_date
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stock_in_report_${fromDate}_to_${toDate}.csv`;
    a.click();
  };

  const exportToPDF = async () => {
    if (!data?.data?.length) return;
    const tableBody = data.data.map(r => [
      r.sl_no, formatDateTime(r.date_time), r.supplier_name, r.invoice_number,
      r.product_name, r.stock_in, r.unit, r.cost_price.toFixed(2), r.mrp.toFixed(2), r.batch_number || '-', r.expiry_date || '-'
    ]);
    const metaLines = [
      `Period: ${fromDate} to ${toDate}`,
      `Total Entries: ${data.summary?.total_entries || data.data.length} | Total Stock In: ${data.summary?.total_stock_in || 0} | Total Value: Rs.${data.summary?.total_value?.toFixed(2) || '0'}`
    ];
    const head = ['Sl', 'Date', 'Supplier', 'Invoice', 'Product', 'Qty', 'Unit', 'Cost', 'MRP', 'Batch', 'Expiry'];
    if (hasIndicScript(data.data.flatMap(r => [r.product_name, r.supplier_name]).join(' '))) {
      await exportTableReportPdf({
        title: 'Stock In Report', metaLines, orientation: 'l',
        filename: `Stock_In_${fromDate}_to_${toDate}.pdf`,
        sections: [{ head, body: tableBody, align: ['c', '', '', '', '', 'r', 'c', 'r', 'r', '', ''] }]
      });
      return;
    }
    const pdf = new jsPDF('l', 'mm', 'a4');
    await setupMultilingualPDF(pdf, data.data.flatMap(r => [r.product_name, r.supplier_name]));
    pdf.setFontSize(14); pdf.setFont('helvetica', 'bold');
    pdf.text('Stock In Report', 14, 15);
    pdf.setFontSize(9); pdf.setFont('helvetica', 'normal');
    pdf.text(`Period: ${fromDate} to ${toDate}`, 14, 22);
    pdf.text(`Total Entries: ${data.summary?.total_entries || data.data.length} | Total Stock In: ${data.summary?.total_stock_in || 0} | Total Value: Rs.${data.summary?.total_value?.toFixed(2) || '0'}`, 14, 28);
    autoTable(pdf, {
      startY: 33,
      head: [head],
      body: tableBody,
      theme: 'grid', headStyles: { fillColor: [79, 70, 229], fontSize: 7 }, bodyStyles: { fontSize: 7 },
      margin: { left: 14, right: 14 },
      didParseCell: autoTableMultilingualHook
    });
    pdf.save(`Stock_In_${fromDate}_to_${toDate}.pdf`);
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-slate-400" />
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
            <span className="text-slate-400">to</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${
              showFilters ? 'bg-violet-50 border-violet-200 text-violet-600' : 'border-slate-200 text-slate-600'
            }`}
          >
            <Filter size={16} />
            Filters
            {(supplierId || productId || invoiceNumber || batchNumber) && <span className="w-2 h-2 bg-violet-600 rounded-full"></span>}
          </button>

          <button
            onClick={fetchReport}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 disabled:opacity-50"
          >
            {loading ? <RefreshCw size={16} className="animate-spin" /> : <Search size={16} />}
            Generate
          </button>

          {data?.data?.length > 0 && (
            <>
              <button onClick={exportToPDF} className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700" data-testid="stockin-export-pdf"><Download size={16} /> PDF</button>
              <button onClick={exportToCSV} className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50"><Download size={16} /> CSV</button>
            </>
          )}
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Supplier</label>
              <select
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                <option value="">All Suppliers</option>
                {suppliers.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Product</label>
              <select
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                <option value="">All Products</option>
                {products.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Invoice Number</label>
              <input
                type="text"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                placeholder="Search invoice..."
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Batch Number</label>
              <input
                type="text"
                value={batchNumber}
                onChange={(e) => setBatchNumber(e.target.value)}
                placeholder="Search batch..."
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      {data?.summary && (
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Total Entries</p>
            <p className="text-2xl font-bold text-slate-800">{data.summary.total_entries}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Total Stock In</p>
            <p className="text-2xl font-bold text-emerald-600">{data.summary.total_stock_in?.toLocaleString('en-IN')}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Total Value</p>
            <p className="text-2xl font-bold text-violet-600">₹{data.summary.total_value?.toLocaleString('en-IN')}</p>
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-3 py-3 font-semibold text-slate-700">Sl</th>
                <th className="text-left px-3 py-3 font-semibold text-slate-700">Date & Time</th>
                <th className="text-left px-3 py-3 font-semibold text-slate-700">Supplier</th>
                <th className="text-left px-3 py-3 font-semibold text-slate-700">Invoice</th>
                <th className="text-left px-3 py-3 font-semibold text-slate-700">Product</th>
                <th className="text-right px-3 py-3 font-semibold text-slate-700">Qty</th>
                <th className="text-right px-3 py-3 font-semibold text-slate-700">Cost</th>
                <th className="text-right px-3 py-3 font-semibold text-slate-700">MRP</th>
                <th className="text-left px-3 py-3 font-semibold text-slate-700">Batch</th>
                <th className="text-left px-3 py-3 font-semibold text-slate-700">Expiry</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="text-center py-8">
                    <RefreshCw className="animate-spin mx-auto text-violet-600" size={24} />
                  </td>
                </tr>
              ) : data?.data?.length > 0 ? (
                data.data.map((row) => (
                  <tr key={row.sl_no} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-3 text-slate-600">{row.sl_no}</td>
                    <td className="px-3 py-3 text-slate-800 text-xs">{formatDateTime(row.date_time)}</td>
                    <td className="px-3 py-3">
                      <p className="font-medium text-slate-800">{row.supplier_name}</p>
                      <p className="text-xs text-slate-500">{row.supplier_mobile}</p>
                    </td>
                    <td className="px-3 py-3 font-mono text-slate-700 text-xs">{row.invoice_number}</td>
                    <td className="px-3 py-3 font-medium text-slate-800">{row.product_name}</td>
                    <td className="px-3 py-3 text-right">
                      <span className="font-semibold text-emerald-600">{row.stock_in}</span>
                      <span className="text-slate-400 text-xs ml-1">{row.unit}</span>
                    </td>
                    <td className="px-3 py-3 text-right text-slate-800">₹{row.cost_price?.toFixed(2)}</td>
                    <td className="px-3 py-3 text-right text-slate-800">₹{row.mrp?.toFixed(2)}</td>
                    <td className="px-3 py-3 font-mono text-xs text-slate-600">{row.batch_number}</td>
                    <td className="px-3 py-3 text-xs text-slate-600">{row.expiry_date !== '-' ? formatDate(row.expiry_date) : '-'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={10} className="text-center py-8 text-slate-400">
                    No data found for the selected filters
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Stock Movement Report Component
const StockMovementReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(getTodayDate());
  const [toDate, setToDate] = useState(getTodayDate());
  const [productId, setProductId] = useState('');
  const [products, setProducts] = useState([]);

  useEffect(() => {
    fetchProducts();
    fetchReport();
  }, []);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/products/enhanced`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('pos_token');
      const params = { from_date: fromDate, to_date: toDate };
      if (productId) params.product_id = productId;

      const response = await axios.get(`${API}/reports/stock-movement`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!data?.data?.length) return;
    const headers = ['Sl.No', 'Product Name', 'Unit', 'Stock In', 'Stock Out', 'Current Balance', 'Sold Out Value'];
    const rows = data.data.map(r => [
      r.sl_no, r.product_name, r.unit, r.stock_in, r.stock_out, r.current_balance, r.sold_out_value?.toFixed(2) || '0.00'
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stock_movement_${fromDate}_to_${toDate}.csv`;
    a.click();
  };

  const exportToPDF = async () => {
    if (!data?.data?.length) return;
    const tableBody = data.data.map(r => [
      r.sl_no, r.product_name, r.unit, r.stock_in, r.stock_out, r.current_balance, (r.sold_out_value || 0).toFixed(2)
    ]);
    const metaLines = [
      `Period: ${fromDate} to ${toDate}`,
      `Products: ${data.summary?.total_products || 0} | Stock In: ${data.summary?.total_stock_in || 0} | Stock Out: ${data.summary?.total_stock_out || 0}`
    ];
    const head = ['Sl', 'Product', 'Unit', 'Stock In', 'Stock Out', 'Balance', 'Sold Value'];
    if (hasIndicScript(data.data.map(r => r.product_name).join(' '))) {
      await exportTableReportPdf({
        title: 'Stock Movement Report', metaLines, orientation: 'l',
        filename: `Stock_Movement_${fromDate}_to_${toDate}.pdf`,
        sections: [{ head, body: tableBody, align: ['c', '', 'c', 'r', 'r', 'r', 'r'] }]
      });
      return;
    }
    const pdf = new jsPDF('l', 'mm', 'a4');
    await setupMultilingualPDF(pdf, data.data.map(r => r.product_name));
    pdf.setFontSize(14); pdf.setFont('helvetica', 'bold');
    pdf.text('Stock Movement Report', 14, 15);
    pdf.setFontSize(9); pdf.setFont('helvetica', 'normal');
    pdf.text(`Period: ${fromDate} to ${toDate}`, 14, 22);
    pdf.text(`Products: ${data.summary?.total_products || 0} | Stock In: ${data.summary?.total_stock_in || 0} | Stock Out: ${data.summary?.total_stock_out || 0}`, 14, 28);
    autoTable(pdf, {
      startY: 33,
      head: [head],
      body: tableBody,
      theme: 'grid', headStyles: { fillColor: [79, 70, 229], fontSize: 8 }, bodyStyles: { fontSize: 7.5 },
      margin: { left: 14, right: 14 },
      didParseCell: autoTableMultilingualHook
    });
    pdf.save(`Stock_Movement_${fromDate}_to_${toDate}.pdf`);
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-slate-400" />
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
            <span className="text-slate-400">to</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          
          <select
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 min-w-[200px]"
          >
            <option value="">All Products</option>
            {products.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>

          <button
            onClick={fetchReport}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 disabled:opacity-50"
          >
            {loading ? <RefreshCw size={16} className="animate-spin" /> : <Search size={16} />}
            Generate
          </button>

          {data?.data?.length > 0 && (
            <>
              <button onClick={exportToPDF} className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700" data-testid="movement-export-pdf"><Download size={16} /> PDF</button>
              <button onClick={exportToCSV} className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50"><Download size={16} /> CSV</button>
            </>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {data?.summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Products with Movement</p>
            <p className="text-2xl font-bold text-slate-800">{data.summary.total_products}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2">
              <TrendingUp size={18} className="text-emerald-600" />
              <p className="text-sm text-slate-500">Total Stock In</p>
            </div>
            <p className="text-2xl font-bold text-emerald-600">{data.summary.total_stock_in?.toLocaleString('en-IN')}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2">
              <TrendingDown size={18} className="text-red-500" />
              <p className="text-sm text-slate-500">Total Stock Out</p>
            </div>
            <p className="text-2xl font-bold text-red-500">{data.summary.total_stock_out?.toLocaleString('en-IN')}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2">
              <DollarSign size={18} className="text-violet-600" />
              <p className="text-sm text-slate-500">Total Sold Value</p>
            </div>
            <p className="text-2xl font-bold text-violet-600">Rs.{data.summary.total_sold_value?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Sl.No</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Product Name</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Unit</th>
                <th className="text-right px-4 py-3 font-semibold text-slate-700">
                  <span className="flex items-center justify-end gap-1">
                    <TrendingUp size={14} className="text-emerald-600" /> Stock In
                  </span>
                </th>
                <th className="text-right px-4 py-3 font-semibold text-slate-700">
                  <span className="flex items-center justify-end gap-1">
                    <TrendingDown size={14} className="text-red-500" /> Stock Out
                  </span>
                </th>
                <th className="text-right px-4 py-3 font-semibold text-slate-700">Current Balance</th>
                <th className="text-right px-4 py-3 font-semibold text-slate-700">
                  <span className="flex items-center justify-end gap-1">
                    <DollarSign size={14} className="text-violet-600" /> Sold Out Value
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-8">
                    <RefreshCw className="animate-spin mx-auto text-violet-600" size={24} />
                  </td>
                </tr>
              ) : data?.data?.length > 0 ? (
                data.data.map((row) => (
                  <tr key={row.sl_no} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-600">{row.sl_no}</td>
                    <td className="px-4 py-3 font-medium text-slate-800">{row.product_name}</td>
                    <td className="px-4 py-3 text-slate-600">{row.unit}</td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-semibold text-emerald-600">+{row.stock_in}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-semibold text-red-500">-{row.stock_out}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-bold ${row.current_balance <= 0 ? 'text-red-600' : 'text-slate-800'}`}>
                        {row.current_balance}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-semibold text-violet-700">Rs.{(row.sold_out_value || 0).toFixed(2)}</span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-slate-400">
                    No stock movement found for the selected period
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// GST Filing Report Component
const GSTReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState('b2b');
  const [fromDate, setFromDate] = useState(() => {
    // Default to first day of current month
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(getTodayDate());

  useEffect(() => {
    fetchReport();
  }, [reportType]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(
        `${API}/reports/${reportType === 'gstr2a' ? 'gst/gstr2a' : `gst/${reportType}`}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { from_date: fromDate, to_date: toDate }
        }
      );
      setData(response.data);
    } catch (error) {
      console.error('Error fetching GST report:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!data) return;
    
    let csvContent = "";
    let filename = "";
    
    if (reportType === 'b2b') {
      filename = `GSTR1_B2B_${fromDate}_${toDate}.csv`;
      csvContent = "GSTIN,Customer Name,Invoice Number,Invoice Date,Invoice Value,Place of Supply,Reverse Charge,Invoice Type,Taxable Value,CGST,SGST,IGST,Cess\n";
      data.invoices.forEach(inv => {
        csvContent += `${inv.gstin},"${inv.customer_name}",${inv.invoice_number},${inv.invoice_date},${inv.invoice_value},${inv.place_of_supply},${inv.reverse_charge},${inv.invoice_type},${inv.taxable_value},${inv.cgst},${inv.sgst},${inv.igst},${inv.cess}\n`;
      });
    } else if (reportType === 'b2c') {
      filename = `GSTR1_B2C_${fromDate}_${toDate}.csv`;
      csvContent = "Customer,Invoice Number,Invoice Date,Taxable Value,CGST,SGST,Total\n";
      (data.invoices || []).forEach(inv => {
        csvContent += `"${inv.customer_name}",${inv.invoice_number},${inv.invoice_date},${inv.taxable_value},${inv.cgst},${inv.sgst},${inv.total_value}\n`;
      });
    } else if (reportType === 'hsn-summary') {
      filename = `GSTR1_HSN_${fromDate}_${toDate}.csv`;
      csvContent = "HSN Code,Description,UQC,Total Quantity,Total Value,Taxable Value,CGST,SGST,IGST,Cess\n";
      data.data.forEach(row => {
        csvContent += `${row.hsn_code},"${row.description}",${row.uqc},${row.total_quantity},${row.total_value},${row.taxable_value},${row.cgst},${row.sgst},${row.igst},${row.cess}\n`;
      });
    } else if (reportType === 'gstr2a') {
      filename = `GSTR2A_${fromDate}_${toDate}.csv`;
      csvContent = "Supplier GSTIN,Supplier Name,GRN No,Invoice No,Invoice Date,Taxable Value,CGST,SGST,IGST,Total Value\n";
      (data.invoices || []).forEach(inv => {
        csvContent += `${inv.supplier_gstin},"${inv.supplier_name}",${inv.grn_number},${inv.invoice_number},${inv.invoice_date},${inv.taxable_value},${inv.cgst},${inv.sgst},${inv.igst},${inv.total_value}\n`;
      });
    }
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  const exportToPDF = async () => {
    if (!data) return;
    const title = reportType === 'b2b' ? 'GSTR-1 B2B Report' : reportType === 'b2c' ? 'GSTR-1 B2C Report' : reportType === 'gstr2a' ? 'GSTR-2A Purchase Report' : 'HSN Summary Report';
    // Build section spec per report type
    let section = null;
    if (reportType === 'b2b' && data.invoices?.length) {
      section = {
        head: ['GSTIN', 'Customer', 'Invoice No', 'Date', 'Value', 'Taxable', 'CGST', 'SGST'],
        body: data.invoices.map(inv => [inv.gstin, inv.customer_name, inv.invoice_number, inv.invoice_date, inv.invoice_value, inv.taxable_value, inv.cgst, inv.sgst]),
        align: ['', '', '', '', 'r', 'r', 'r', 'r']
      };
    } else if (reportType === 'b2c' && data.invoices?.length) {
      section = {
        head: ['Customer', 'Invoice No', 'Date', 'Taxable', 'CGST', 'SGST', 'Total'],
        body: data.invoices.map(inv => [inv.customer_name, inv.invoice_number, inv.invoice_date, inv.taxable_value, inv.cgst, inv.sgst, inv.total_value]),
        align: ['', '', '', 'r', 'r', 'r', 'r']
      };
    } else if (reportType === 'hsn-summary' && data.data?.length) {
      section = {
        head: ['HSN', 'Description', 'Qty', 'Total Value', 'Taxable', 'CGST', 'SGST'],
        body: data.data.map(r => [r.hsn_code, r.description, r.total_quantity, r.total_value, r.taxable_value, r.cgst, r.sgst]),
        align: ['', '', 'r', 'r', 'r', 'r', 'r']
      };
    } else if (reportType === 'gstr2a' && data.invoices?.length) {
      section = {
        head: ['Supplier GSTIN', 'Supplier', 'GRN No', 'Invoice No', 'Date', 'Taxable', 'CGST', 'SGST', 'Total'],
        body: data.invoices.map(inv => [inv.supplier_gstin, inv.supplier_name, inv.grn_number, inv.invoice_number, inv.invoice_date, inv.taxable_value, inv.cgst, inv.sgst, inv.total_value]),
        align: ['', '', '', '', '', 'r', 'r', 'r', 'r'],
        headColor: '#f59e0b'
      };
    }
    const sampleText = [
      ...(data.invoices || []).flatMap(i => [i.customer_name, i.supplier_name]),
      ...(data.data || []).map(r => r.description)
    ].filter(Boolean).join(' ');
    if (section && hasIndicScript(sampleText)) {
      await exportTableReportPdf({
        title, metaLines: [`Period: ${fromDate} to ${toDate}`], orientation: 'l',
        filename: `GST_${reportType}_${fromDate}_to_${toDate}.pdf`,
        sections: [section]
      });
      return;
    }
    const pdf = new jsPDF('l', 'mm', 'a4');
    // Pre-load fonts for any multilingual customer/supplier/HSN description text
    const samples = [];
    if (data.invoices) samples.push(...data.invoices.flatMap(i => [i.customer_name, i.supplier_name]));
    if (data.data) samples.push(...data.data.map(r => r.description));
    await setupMultilingualPDF(pdf, samples);
    pdf.setFontSize(14); pdf.setFont('helvetica', 'bold');
    pdf.text(title, 14, 15);
    pdf.setFontSize(9); pdf.setFont('helvetica', 'normal');
    pdf.text(`Period: ${fromDate} to ${toDate}`, 14, 22);

    if (reportType === 'b2b' && data.invoices?.length) {
      autoTable(pdf, {
        startY: 28,
        head: [['GSTIN', 'Customer', 'Invoice No', 'Date', 'Value', 'Taxable', 'CGST', 'SGST']],
        body: data.invoices.map(inv => [inv.gstin, inv.customer_name, inv.invoice_number, inv.invoice_date, inv.invoice_value, inv.taxable_value, inv.cgst, inv.sgst]),
        theme: 'grid', headStyles: { fillColor: [79, 70, 229], fontSize: 7 }, bodyStyles: { fontSize: 7 }, margin: { left: 14, right: 14 },
        didParseCell: autoTableMultilingualHook
      });
    } else if (reportType === 'b2c' && data.invoices?.length) {
      autoTable(pdf, {
        startY: 28,
        head: [['Customer', 'Invoice No', 'Date', 'Taxable', 'CGST', 'SGST', 'Total']],
        body: data.invoices.map(inv => [inv.customer_name, inv.invoice_number, inv.invoice_date, inv.taxable_value, inv.cgst, inv.sgst, inv.total_value]),
        theme: 'grid', headStyles: { fillColor: [79, 70, 229], fontSize: 8 }, bodyStyles: { fontSize: 7.5 }, margin: { left: 14, right: 14 },
        didParseCell: autoTableMultilingualHook
      });
    } else if (reportType === 'hsn-summary' && data.data?.length) {
      autoTable(pdf, {
        startY: 28,
        head: [['HSN', 'Description', 'Qty', 'Total Value', 'Taxable', 'CGST', 'SGST']],
        body: data.data.map(r => [r.hsn_code, r.description, r.total_quantity, r.total_value, r.taxable_value, r.cgst, r.sgst]),
        theme: 'grid', headStyles: { fillColor: [79, 70, 229], fontSize: 7 }, bodyStyles: { fontSize: 7 }, margin: { left: 14, right: 14 },
        didParseCell: autoTableMultilingualHook
      });
    } else if (reportType === 'gstr2a' && data.invoices?.length) {
      autoTable(pdf, {
        startY: 28,
        head: [['Supplier GSTIN', 'Supplier', 'GRN No', 'Invoice No', 'Date', 'Taxable', 'CGST', 'SGST', 'Total']],
        body: data.invoices.map(inv => [inv.supplier_gstin, inv.supplier_name, inv.grn_number, inv.invoice_number, inv.invoice_date, inv.taxable_value, inv.cgst, inv.sgst, inv.total_value]),
        theme: 'grid', headStyles: { fillColor: [245, 158, 11], fontSize: 7 }, bodyStyles: { fontSize: 7 }, margin: { left: 14, right: 14 },
        didParseCell: autoTableMultilingualHook
      });
    }
    pdf.save(`GST_${reportType}_${fromDate}_to_${toDate}.pdf`);
  };

  const formatCurrency = (amount) => `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

  return (
    <div className="space-y-4">
      {/* Report Type Selector and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        {/* Report Type Pills */}
        <div className="flex gap-2 bg-slate-100 p-1 rounded-lg">
          <button
            onClick={() => setReportType('b2b')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              reportType === 'b2b' ? 'bg-white text-violet-600 shadow-sm' : 'text-slate-600 hover:text-violet-600'
            }`}
          >
            B2B (Business)
          </button>
          <button
            onClick={() => setReportType('b2c')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              reportType === 'b2c' ? 'bg-white text-violet-600 shadow-sm' : 'text-slate-600 hover:text-violet-600'
            }`}
          >
            B2C (Consumer)
          </button>
          <button
            onClick={() => setReportType('hsn-summary')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              reportType === 'hsn-summary' ? 'bg-white text-violet-600 shadow-sm' : 'text-slate-600 hover:text-violet-600'
            }`}
          >
            HSN Summary
          </button>
          <button
            onClick={() => setReportType('gstr2a')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              reportType === 'gstr2a' ? 'bg-white text-amber-600 shadow-sm' : 'text-slate-600 hover:text-amber-600'
            }`}
          >
            GSTR-2A (Purchase)
          </button>
        </div>
        
        {/* Date Filters */}
        <div className="flex items-center gap-2 flex-1">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-slate-400" />
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
            <span className="text-slate-400">to</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          <button
            onClick={fetchReport}
            disabled={loading}
            className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 flex items-center gap-2"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Generate
          </button>
          {data && (
            <>
              <button onClick={exportToPDF} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2" data-testid="gst-export-pdf"><Download size={16} /> PDF</button>
              <button onClick={exportToCSV} className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 flex items-center gap-2"><Download size={16} /> CSV</button>
            </>
          )}
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <p className="text-sm text-blue-700">
          <strong>GST Filing Reports (GSTR-1):</strong>{' '}
          {reportType === 'b2b' && 'B2B - Sales to registered businesses with GSTIN. Export for GSTR-1 B2B section.'}
          {reportType === 'b2c' && 'B2C - Invoice-wise sales to consumers without GSTIN. Export for GSTR-1 B2C section.'}
          {reportType === 'hsn-summary' && 'HSN Summary - All sales grouped by HSN code. Required for GSTR-1 HSN Summary.'}
        </p>
      </div>

      {/* Summary Cards */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
            <p className="text-xs text-violet-600">Total Invoices</p>
            <p className="text-xl font-bold text-violet-700">{data.summary?.total_invoices || data.summary?.total_hsn_codes || 0}</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <p className="text-xs text-slate-600">Taxable Value</p>
            <p className="text-xl font-bold text-slate-700">{formatCurrency(data.summary?.total_taxable_value)}</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <p className="text-xs text-emerald-600">CGST</p>
            <p className="text-xl font-bold text-emerald-700">{formatCurrency(data.summary?.total_cgst)}</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-600">SGST</p>
            <p className="text-xl font-bold text-blue-700">{formatCurrency(data.summary?.total_sgst)}</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-xs text-amber-600">Total Value</p>
            <p className="text-xl font-bold text-amber-700">{formatCurrency(data.summary?.total_invoice_value || (data.summary?.total_taxable_value + data.summary?.total_cgst + data.summary?.total_sgst))}</p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-violet-200 border-t-violet-600"></div>
        </div>
      )}

      {/* B2B Report Table */}
      {!loading && data && reportType === 'b2b' && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-y border-slate-200">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">#</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">GSTIN</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Customer</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Invoice No</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Date</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Taxable</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">CGST</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">SGST</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.invoices?.map((inv, idx) => (
                <tr key={`row-${idx}`} className="hover:bg-slate-50">
                  <td className="px-3 py-2 text-slate-500">{idx + 1}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-700">{inv.gstin}</td>
                  <td className="px-3 py-2 text-slate-700">{inv.customer_name}</td>
                  <td className="px-3 py-2 text-violet-600 font-medium">{inv.invoice_number}</td>
                  <td className="px-3 py-2 text-slate-500">{inv.invoice_date}</td>
                  <td className="px-3 py-2 text-right text-slate-700">{formatCurrency(inv.taxable_value)}</td>
                  <td className="px-3 py-2 text-right text-emerald-600">{formatCurrency(inv.cgst)}</td>
                  <td className="px-3 py-2 text-right text-blue-600">{formatCurrency(inv.sgst)}</td>
                  <td className="px-3 py-2 text-right font-semibold text-slate-800">{formatCurrency(inv.invoice_value)}</td>
                </tr>
              ))}
              {data.invoices?.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center py-8 text-slate-400">
                    No B2B invoices found. B2B invoices require customers with valid GSTIN.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* B2C Report Table - invoice-level detail */}
      {!loading && data && reportType === 'b2c' && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-y border-slate-200">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">#</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Customer</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Invoice No</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Date</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Taxable</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">CGST</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">SGST</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.invoices?.map((inv, idx) => (
                <tr key={`row-${idx}`} className="hover:bg-slate-50" data-testid={`b2c-row-${idx}`}>
                  <td className="px-3 py-2 text-slate-500">{idx + 1}</td>
                  <td className="px-3 py-2 text-slate-700">{inv.customer_name}</td>
                  <td className="px-3 py-2 text-violet-600 font-medium">{inv.invoice_number}</td>
                  <td className="px-3 py-2 text-slate-500">{inv.invoice_date}</td>
                  <td className="px-3 py-2 text-right text-slate-700">{formatCurrency(inv.taxable_value)}</td>
                  <td className="px-3 py-2 text-right text-emerald-600">{formatCurrency(inv.cgst)}</td>
                  <td className="px-3 py-2 text-right text-blue-600">{formatCurrency(inv.sgst)}</td>
                  <td className="px-3 py-2 text-right font-semibold text-slate-800">{formatCurrency(inv.total_value)}</td>
                </tr>
              ))}
              {(!data.invoices || data.invoices.length === 0) && (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-slate-400">
                    No B2C sales found for the selected period
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* HSN Summary Table */}
      {!loading && data && reportType === 'hsn-summary' && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-y border-slate-200">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">#</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">HSN Code</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Description</th>
                <th className="px-3 py-2 text-center text-xs font-semibold text-slate-600">UQC</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Quantity</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Taxable Value</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">CGST</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">SGST</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.data?.map((row, idx) => (
                <tr key={`row-${idx}`} className="hover:bg-slate-50">
                  <td className="px-3 py-2 text-slate-500">{idx + 1}</td>
                  <td className="px-3 py-2 font-mono text-violet-600 font-medium">{row.hsn_code}</td>
                  <td className="px-3 py-2 text-slate-700 truncate max-w-[200px]">{row.description}</td>
                  <td className="px-3 py-2 text-center text-slate-500">{row.uqc}</td>
                  <td className="px-3 py-2 text-right text-slate-700">{row.total_quantity}</td>
                  <td className="px-3 py-2 text-right text-slate-700">{formatCurrency(row.taxable_value)}</td>
                  <td className="px-3 py-2 text-right text-emerald-600">{formatCurrency(row.cgst)}</td>
                  <td className="px-3 py-2 text-right text-blue-600">{formatCurrency(row.sgst)}</td>
                  <td className="px-3 py-2 text-right font-semibold text-slate-800">{formatCurrency(row.total_value)}</td>
                </tr>
              ))}
              {data.data?.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center py-8 text-slate-400">
                    No HSN data found. Ensure products have HSN codes assigned.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      
      {/* GSTR-2A Purchase Report Table */}
      {!loading && data && reportType === 'gstr2a' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <div className="bg-amber-50 p-3 rounded-lg"><p className="text-xs text-slate-500">Invoices</p><p className="text-lg font-bold text-amber-700">{data.summary?.total_invoices || 0}</p></div>
            <div className="bg-amber-50 p-3 rounded-lg"><p className="text-xs text-slate-500">Taxable Value</p><p className="text-lg font-bold text-amber-700">Rs.{(data.summary?.total_taxable_value || 0).toFixed(2)}</p></div>
            <div className="bg-amber-50 p-3 rounded-lg"><p className="text-xs text-slate-500">CGST</p><p className="text-lg font-bold text-amber-700">Rs.{(data.summary?.total_cgst || 0).toFixed(2)}</p></div>
            <div className="bg-amber-50 p-3 rounded-lg"><p className="text-xs text-slate-500">SGST</p><p className="text-lg font-bold text-amber-700">Rs.{(data.summary?.total_sgst || 0).toFixed(2)}</p></div>
            <div className="bg-amber-50 p-3 rounded-lg"><p className="text-xs text-slate-500">Total Value</p><p className="text-lg font-bold text-amber-700">Rs.{(data.summary?.total_value || 0).toFixed(2)}</p></div>
          </div>
          <div className="overflow-x-auto bg-white rounded-xl border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-amber-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Supplier GSTIN</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Supplier</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">GRN No</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Invoice No</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Date</th>
                  <th className="px-3 py-2 text-right font-medium text-slate-600">Taxable</th>
                  <th className="px-3 py-2 text-right font-medium text-slate-600">CGST</th>
                  <th className="px-3 py-2 text-right font-medium text-slate-600">SGST</th>
                  <th className="px-3 py-2 text-right font-medium text-slate-600">Total</th>
                </tr>
              </thead>
              <tbody>
                {(data.invoices || []).map((inv, i) => (
                  <tr key={`gstr2a-${i}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono text-xs">{inv.supplier_gstin || '-'}</td>
                    <td className="px-3 py-2">{inv.supplier_name}</td>
                    <td className="px-3 py-2">{inv.grn_number}</td>
                    <td className="px-3 py-2">{inv.invoice_number || '-'}</td>
                    <td className="px-3 py-2 text-xs">{inv.invoice_date}</td>
                    <td className="px-3 py-2 text-right">{(inv.taxable_value || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right">{(inv.cgst || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right">{(inv.sgst || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right font-medium">{(inv.total_value || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Format date for display in reports
const formatDateTimeReport = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const d = date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const t = date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  return `${d} ${t}`;
};

// Product-wise Sales Report Component
const ProductWiseSalesReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(getTodayDate());
  const [toDate, setToDate] = useState(getTodayDate());
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [viewMode, setViewMode] = useState('breakup'); // 'breakup' or 'group_by_product'

  useEffect(() => {
    const token = localStorage.getItem('pos_token');
    Promise.all([
      axios.get(`${API}/products/enhanced`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/customers`, { headers: { Authorization: `Bearer ${token}` } })
    ]).then(([prodRes, custRes]) => {
      setProducts(prodRes.data || []);
      setCustomers(custRes.data || []);
    }).catch(() => {});
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('pos_token');
      const params = new URLSearchParams({ from_date: fromDate, to_date: toDate, group_by: 'product' });
      if (selectedProduct) params.append('product_id', selectedProduct);
      if (selectedCustomer) params.append('customer_id', selectedCustomer);
      const resp = await axios.get(`${API}/reports/product-wise-sales?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(resp.data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Build grouped data from backend response
  const getGroupedData = () => {
    if (!data?.groups) return [];
    const productMap = {};
    // Enrich groups with actual price from rows
    (data.rows || []).forEach(row => {
      const name = row.product_name;
      if (!productMap[name]) {
        productMap[name] = { product_name: name, total_qty: 0, total_amount: 0, rates: [], actual_price: row.rate };
      }
      productMap[name].total_qty += row.quantity;
      productMap[name].total_amount += row.amount;
      productMap[name].rates.push(row.rate);
    });
    return Object.values(productMap).map(p => ({
      product_name: p.product_name,
      total_qty: p.total_qty,
      total_amount: p.total_amount,
      avg_price: p.rates.length > 0 ? (p.total_amount / p.total_qty) : 0,
      actual_price: p.actual_price
    })).sort((a, b) => b.total_amount - a.total_amount);
  };

  const exportCSV = () => {
    if (!data?.rows?.length) return;
    if (viewMode === 'group_by_product') {
      const grouped = getGroupedData();
      const headers = ['S.No', 'Product Name', 'Total Qty', 'Total Amount', 'Avg Price', 'Actual Price'];
      const csvRows = [headers.join(',')];
      grouped.forEach((row, i) => {
        csvRows.push([i + 1, `"${row.product_name}"`, row.total_qty, row.total_amount.toFixed(2), row.avg_price.toFixed(2), row.actual_price.toFixed(2)].join(','));
      });
      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `Product_GroupBy_${fromDate}_to_${toDate}.csv`; a.click();
      URL.revokeObjectURL(url);
    } else {
      const headers = ['S.No', 'Date & Time', 'Product Name', 'Bill Number', 'Customer', 'Qty', 'Rate', 'Discount', 'Amount'];
      const csvRows = [headers.join(',')];
      data.rows.forEach((row, i) => {
        csvRows.push([i + 1, `"${formatDateTimeReport(row.date)}"`, `"${row.product_name}"`, row.bill_number, `"${row.customer_name}"`, row.quantity, row.rate.toFixed(2), row.discount.toFixed(2), row.amount.toFixed(2)].join(','));
      });
      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `Product_Sales_${fromDate}_to_${toDate}.csv`; a.click();
      URL.revokeObjectURL(url);
    }
  };

  const exportPDF = async () => {
    if (!data?.rows?.length) return;
    const metaLines = [
      `Period: ${fromDate} to ${toDate}`,
      `Total Items: ${data.summary.total_rows} | Total Qty: ${data.summary.total_qty} | Total Amount: Rs.${data.summary.total_amount.toFixed(2)}`
    ];
    const isGrouped = viewMode === 'group_by_product';
    const grouped = isGrouped ? getGroupedData() : null;
    const head = isGrouped
      ? ['S.No', 'Product', 'Total Qty', 'Avg Price', 'Actual Price', 'Total Amount']
      : ['S.No', 'Date & Time', 'Product', 'Bill No', 'Customer', 'Qty', 'Rate', 'Discount', 'Amount'];
    const tableBody = isGrouped
      ? grouped.map((row, i) => [i + 1, row.product_name, row.total_qty, row.avg_price.toFixed(2), row.actual_price.toFixed(2), row.total_amount.toFixed(2)])
      : data.rows.map((row, i) => [i + 1, formatDateTimeReport(row.date), row.product_name, row.bill_number, row.customer_name, row.quantity, row.rate.toFixed(2), row.discount.toFixed(2), row.amount.toFixed(2)]);
    if (hasIndicScript(data.rows.flatMap(r => [r.product_name, r.customer_name]).join(' '))) {
      await exportTableReportPdf({
        title: 'Product-wise Sales Report', metaLines, orientation: 'l',
        filename: `Product_Sales_${fromDate}_to_${toDate}.pdf`,
        sections: [{
          head, body: tableBody,
          align: isGrouped ? ['c', '', 'r', 'r', 'r', 'r'] : ['c', '', '', '', '', 'r', 'r', 'r', 'r']
        }]
      });
      return;
    }
    const pdf = new jsPDF('l', 'mm', 'a4');
    await setupMultilingualPDF(pdf, data.rows.flatMap(r => [r.product_name, r.customer_name]));
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Product-wise Sales Report', 14, 15);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Period: ${fromDate} to ${toDate}`, 14, 22);
    pdf.text(`Total Items: ${data.summary.total_rows} | Total Qty: ${data.summary.total_qty} | Total Amount: Rs.${data.summary.total_amount.toFixed(2)}`, 14, 28);

    if (viewMode === 'group_by_product') {
      autoTable(pdf, {
        startY: 33,
        head: [head],
        body: tableBody,
        theme: 'grid',
        headStyles: { fillColor: [79, 70, 229], fontSize: 8 },
        bodyStyles: { fontSize: 7.5 },
        margin: { left: 14, right: 14 },
        didParseCell: autoTableMultilingualHook
      });
    } else {
      autoTable(pdf, {
        startY: 33,
        head: [head],
        body: tableBody,
        theme: 'grid',
        headStyles: { fillColor: [79, 70, 229], fontSize: 8 },
        bodyStyles: { fontSize: 7.5 },
        margin: { left: 14, right: 14 },
        didParseCell: autoTableMultilingualHook
      });
    }

    pdf.save(`Product_Sales_${fromDate}_to_${toDate}.pdf`);
  };

  // 80mm Thermal Print for Group By Product view
  const handleThermalPrint = () => {
    const grouped = getGroupedData();
    if (!grouped.length) return;
    const totalAmount = grouped.reduce((s, r) => s + r.total_amount, 0);
    const now = new Date();
    const printDate = now.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const printTime = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

    const printWindow = window.open('', '_blank', 'width=320,height=700');
    printWindow.document.write(`<!DOCTYPE html><html><head><title>Product Sales Report</title>
    <style>
      * { margin:0; padding:0; box-sizing:border-box; }
      body { font-family: 'Courier New', monospace; width:80mm; padding:3mm; color:#000; font-weight:bold; }
      .center { text-align:center; }
      .right { text-align:right; }
      .sep { border-top:1px dashed #000; margin:4px 0; }
      .bold { font-weight:900; }
      table { width:100%; border-collapse:collapse; font-size:9px; }
      th { text-align:left; padding:2px 1px; border-bottom:1px solid #000; font-weight:900; font-size:8px; }
      td { padding:2px 1px; border-bottom:1px dotted #ccc; font-size:9px; }
      td.r, th.r { text-align:right; }
      .total-row { border-top:1px solid #000; font-weight:900; font-size:11px; }
      .header { font-size:12px; font-weight:900; margin-bottom:2px; }
      .sub { font-size:9px; margin-bottom:2px; }
    </style></head><body>
      <div class="center header">PRODUCT SALES REPORT</div>
      <div class="center sub">From: ${fromDate} To: ${toDate}</div>
      <div class="center sub">Printed: ${printDate} ${printTime}</div>
      <div class="sep"></div>
      <table>
        <thead><tr><th>Sl</th><th>Product</th><th class="r">Qty</th><th class="r">Amount</th><th class="r">Avg</th><th class="r">Price</th></tr></thead>
        <tbody>
          ${grouped.map((r, i) => `<tr><td>${i+1}</td><td>${r.product_name}</td><td class="r">${r.total_qty}</td><td class="r">${r.total_amount.toFixed(2)}</td><td class="r">${r.avg_price.toFixed(2)}</td><td class="r">${r.actual_price.toFixed(2)}</td></tr>`).join('')}
        </tbody>
      </table>
      <div class="sep"></div>
      <div style="display:flex;justify-content:space-between;font-size:11px;font-weight:900;">
        <span>TOTAL SALES</span><span>Rs. ${totalAmount.toFixed(2)}</span>
      </div>
      <div class="sep"></div>
      <div class="center sub" style="margin-top:4px;">--- End of Report ---</div>
    </body></html>`);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 400);
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-500">View Mode</label>
          <select value={viewMode} onChange={e => setViewMode(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-medium" data-testid="product-report-view-mode">
            <option value="breakup">Breakup</option>
            <option value="group_by_product">Group By Product</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500">From Date</label>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="product-report-from" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500">To Date</label>
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="product-report-to" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500">Product</label>
          <select value={selectedProduct} onChange={e => setSelectedProduct(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="product-report-product">
            <option value="">All Products</option>
            {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500">Customer</label>
          <SearchableCustomerSelect
            customers={customers}
            value={selectedCustomer}
            onChange={(val) => setSelectedCustomer(val)}
            placeholder="All Customers"
            testId="product-report-customer"
          />
        </div>
        <div className="flex items-end">
          <button onClick={fetchReport} disabled={loading} className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 text-sm font-medium" data-testid="product-report-generate">
            {loading ? <RefreshCw size={16} className="animate-spin" /> : <Search size={16} />}
            Generate
          </button>
        </div>
      </div>

      {/* Results */}
      {data && (
        <div className="space-y-3">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-violet-50 p-3 rounded-lg"><p className="text-xs text-slate-500">{viewMode === 'group_by_product' ? 'Products' : 'Total Entries'}</p><p className="text-lg font-bold text-violet-700">{viewMode === 'group_by_product' ? Object.keys(data.groups || {}).length : data.summary.total_rows}</p></div>
            <div className="bg-blue-50 p-3 rounded-lg"><p className="text-xs text-slate-500">Total Quantity</p><p className="text-lg font-bold text-blue-700">{data.summary.total_qty}</p></div>
            <div className="bg-green-50 p-3 rounded-lg"><p className="text-xs text-slate-500">Total Amount</p><p className="text-lg font-bold text-green-700">Rs.{data.summary.total_amount.toFixed(2)}</p></div>
            <div className="bg-orange-50 p-3 rounded-lg"><p className="text-xs text-slate-500">Total Discount</p><p className="text-lg font-bold text-orange-700">Rs.{data.summary.total_discount.toFixed(2)}</p></div>
          </div>

          {/* Export Buttons */}
          <div className="flex gap-2 justify-end">
            {viewMode === 'group_by_product' && (
              <button onClick={handleThermalPrint} className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 text-white rounded-lg text-sm hover:bg-slate-900" data-testid="product-report-thermal-print">
                <FileText size={14} /> 80mm Print
              </button>
            )}
            <button onClick={exportPDF} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700"><Download size={14} /> PDF</button>
            <button onClick={exportCSV} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"><Download size={14} /> CSV</button>
          </div>

          {/* Data Table - Breakup View */}
          {viewMode === 'breakup' && (
            <div className="overflow-x-auto bg-white rounded-xl border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">S.No</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Date & Time</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Product</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Bill No</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Customer</th>
                    <th className="px-3 py-2 text-right font-medium text-slate-600">Qty</th>
                    <th className="px-3 py-2 text-right font-medium text-slate-600">Rate</th>
                    <th className="px-3 py-2 text-right font-medium text-slate-600">Discount</th>
                    <th className="px-3 py-2 text-right font-medium text-slate-600">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row, i) => (
                    <tr key={`row-${i}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-2">{i + 1}</td>
                      <td className="px-3 py-2 text-xs">{formatDateTimeReport(row.date)}</td>
                      <td className="px-3 py-2 font-medium">{row.product_name}</td>
                      <td className="px-3 py-2">{row.bill_number}</td>
                      <td className="px-3 py-2">{row.customer_name}</td>
                      <td className="px-3 py-2 text-right">{row.quantity}</td>
                      <td className="px-3 py-2 text-right">{row.rate.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right">{row.discount.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right font-medium">{row.amount.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.rows.length === 0 && <p className="text-center py-8 text-slate-400">No sales data found for selected filters</p>}
            </div>
          )}

          {/* Data Table - Group By Product View */}
          {viewMode === 'group_by_product' && (
            <div className="overflow-x-auto bg-white rounded-xl border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">S.No</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Product Name</th>
                    <th className="px-3 py-2 text-right font-medium text-slate-600">Total Qty</th>
                    <th className="px-3 py-2 text-right font-medium text-slate-600">Total Amount</th>
                    <th className="px-3 py-2 text-right font-medium text-slate-600">Avg Price</th>
                    <th className="px-3 py-2 text-right font-medium text-slate-600">Actual Price</th>
                  </tr>
                </thead>
                <tbody>
                  {getGroupedData().map((row, i) => (
                    <tr key={`row-${i}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-2">{i + 1}</td>
                      <td className="px-3 py-2 font-medium">{row.product_name}</td>
                      <td className="px-3 py-2 text-right">{row.total_qty}</td>
                      <td className="px-3 py-2 text-right font-medium">{row.total_amount.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right">{row.avg_price.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right">{row.actual_price.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-100 border-t-2 border-slate-300">
                  <tr>
                    <td colSpan={2} className="px-3 py-2 font-bold text-slate-800">TOTAL</td>
                    <td className="px-3 py-2 text-right font-bold">{data.summary.total_qty}</td>
                    <td className="px-3 py-2 text-right font-bold text-green-700">Rs.{data.summary.total_amount.toFixed(2)}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
              {getGroupedData().length === 0 && <p className="text-center py-8 text-slate-400">No sales data found for selected filters</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Customer-wise Sales Report Component
const CustomerWiseSalesReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(getTodayDate());
  const [toDate, setToDate] = useState(getTodayDate());
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [mode, setMode] = useState('bill_wise');

  useEffect(() => {
    const token = localStorage.getItem('pos_token');
    axios.get(`${API}/customers`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setCustomers(r.data || []))
      .catch(() => {});
  }, []);

  const fetchReport = async () => {
    if (!selectedCustomer) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('pos_token');
      const params = new URLSearchParams({ from_date: fromDate, to_date: toDate, customer_id: selectedCustomer, mode });
      const resp = await axios.get(`${API}/reports/customer-wise-sales?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(resp.data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    if (!data?.rows?.length) return;
    let csvRows = [];
    if (data.mode === 'bill_wise') {
      csvRows.push(['S.No', 'Date', 'Bill Number', 'Items', 'Subtotal', 'Discount', 'Tax', 'Grand Total'].join(','));
      data.rows.forEach((row, i) => {
        csvRows.push([i + 1, `"${formatDateTimeReport(row.date)}"`, row.bill_number, row.items_count, row.subtotal.toFixed(2), row.discount.toFixed(2), row.tax.toFixed(2), row.grand_total.toFixed(2)].join(','));
      });
    } else {
      csvRows.push(['S.No', 'Product', 'Total Qty', 'Total Amount', 'Total Discount'].join(','));
      data.rows.forEach((row, i) => {
        csvRows.push([i + 1, `"${row.product_name}"`, row.total_qty, row.total_amount.toFixed(2), row.total_discount.toFixed(2)].join(','));
      });
    }
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Customer_Sales_${data.customer_name}_${fromDate}_to_${toDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = async () => {
    if (!data?.rows?.length) return;
    const billWise = data.mode === 'bill_wise';
    const head = billWise
      ? ['S.No', 'Date', 'Bill No', 'Items', 'Subtotal', 'Discount', 'Tax', 'Grand Total']
      : ['S.No', 'Product', 'Total Qty', 'Total Amount', 'Total Discount'];
    const tableBody = billWise
      ? data.rows.map((r, i) => [i + 1, formatDateTimeReport(r.date), r.bill_number, r.items_count, r.subtotal.toFixed(2), r.discount.toFixed(2), r.tax.toFixed(2), r.grand_total.toFixed(2)])
      : data.rows.map((r, i) => [i + 1, r.product_name, r.total_qty, r.total_amount.toFixed(2), r.total_discount.toFixed(2)]);
    const summaryMeta = billWise
      ? `Total Bills: ${data.summary.total_bills} | Total Amount: Rs.${data.summary.total_amount.toFixed(2)}`
      : `Total Products: ${data.summary.total_products} | Total Amount: Rs.${data.summary.total_amount.toFixed(2)}`;
    const metaLines = [
      `Customer: ${data.customer_name} | Period: ${fromDate} to ${toDate} | Mode: ${billWise ? 'Bill Wise' : 'Product Wise'}`,
      summaryMeta
    ];
    if (hasIndicScript([data.customer_name, ...data.rows.map(r => r.product_name)].filter(Boolean).join(' '))) {
      await exportTableReportPdf({
        title: 'Customer-wise Sales Report', metaLines, orientation: 'p',
        filename: `Customer_Sales_${data.customer_name}_${fromDate}_to_${toDate}.pdf`,
        sections: [{
          head, body: tableBody,
          align: billWise ? ['c', '', '', 'c', 'r', 'r', 'r', 'r'] : ['c', '', 'r', 'r', 'r']
        }]
      });
      return;
    }
    const pdf = new jsPDF('p', 'mm', 'a4');
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Customer-wise Sales Report', 14, 15);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Customer: ${data.customer_name} | Period: ${fromDate} to ${toDate} | Mode: ${billWise ? 'Bill Wise' : 'Product Wise'}`, 14, 22);
    pdf.text(summaryMeta, 14, 28);
    autoTable(pdf, {
      startY: 33,
      head: [head],
      body: tableBody,
      theme: 'grid',
      headStyles: { fillColor: [79, 70, 229], fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      margin: { left: 14, right: 14 },
      didParseCell: autoTableMultilingualHook
    });

    pdf.save(`Customer_Sales_${data.customer_name}_${fromDate}_to_${toDate}.pdf`);
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-500">From Date</label>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="customer-report-from" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500">To Date</label>
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="customer-report-to" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500">Customer</label>
          <SearchableCustomerSelect
            customers={customers}
            value={selectedCustomer}
            onChange={(val) => setSelectedCustomer(val)}
            placeholder="Select Customer"
            testId="customer-report-select"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500">Breakup Mode</label>
          <select value={mode} onChange={e => setMode(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="customer-report-mode">
            <option value="bill_wise">Bill Wise</option>
            <option value="product_wise">Product Wise</option>
          </select>
        </div>
        <div className="flex items-end">
          <button onClick={fetchReport} disabled={loading || !selectedCustomer} className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 text-sm font-medium" data-testid="customer-report-generate">
            {loading ? <RefreshCw size={16} className="animate-spin" /> : <Search size={16} />}
            Generate
          </button>
        </div>
      </div>

      {/* Results */}
      {data && (
        <div className="space-y-3">
          {/* Summary */}
          <div className="flex items-center justify-between bg-violet-50 p-4 rounded-xl">
            <div>
              <p className="font-bold text-violet-700 text-lg">{data.customer_name}</p>
              <p className="text-sm text-slate-500">{data.mode === 'bill_wise' ? `${data.summary.total_bills} bills` : `${data.summary.total_products} products`} | Rs.{data.summary.total_amount.toFixed(2)} total</p>
            </div>
            <div className="flex gap-2">
              <button onClick={exportPDF} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700"><Download size={14} /> PDF</button>
              <button onClick={exportCSV} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"><Download size={14} /> CSV</button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto bg-white rounded-xl border border-slate-200">
            <table className="w-full text-sm">
              {data.mode === 'bill_wise' ? (
                <>
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">S.No</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Date & Time</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Bill No</th>
                      <th className="px-3 py-2 text-center font-medium text-slate-600">Items</th>
                      <th className="px-3 py-2 text-right font-medium text-slate-600">Subtotal</th>
                      <th className="px-3 py-2 text-right font-medium text-slate-600">Discount</th>
                      <th className="px-3 py-2 text-right font-medium text-slate-600">Tax</th>
                      <th className="px-3 py-2 text-right font-medium text-slate-600">Grand Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((row, i) => (
                      <tr key={`row-${i}`} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-3 py-2">{i + 1}</td>
                        <td className="px-3 py-2 text-xs">{formatDateTimeReport(row.date)}</td>
                        <td className="px-3 py-2 font-medium">{row.bill_number}</td>
                        <td className="px-3 py-2 text-center">{row.items_count}</td>
                        <td className="px-3 py-2 text-right">{row.subtotal.toFixed(2)}</td>
                        <td className="px-3 py-2 text-right">{row.discount.toFixed(2)}</td>
                        <td className="px-3 py-2 text-right">{row.tax.toFixed(2)}</td>
                        <td className="px-3 py-2 text-right font-bold">{row.grand_total.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </>
              ) : (
                <>
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">S.No</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Product</th>
                      <th className="px-3 py-2 text-right font-medium text-slate-600">Total Qty</th>
                      <th className="px-3 py-2 text-right font-medium text-slate-600">Total Amount</th>
                      <th className="px-3 py-2 text-right font-medium text-slate-600">Total Discount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((row, i) => (
                      <tr key={`row-${i}`} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-3 py-2">{i + 1}</td>
                        <td className="px-3 py-2 font-medium">{row.product_name}</td>
                        <td className="px-3 py-2 text-right">{row.total_qty}</td>
                        <td className="px-3 py-2 text-right font-bold">{row.total_amount.toFixed(2)}</td>
                        <td className="px-3 py-2 text-right">{row.total_discount.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </>
              )}
            </table>
            {data.rows.length === 0 && <p className="text-center py-8 text-slate-400">No data found for selected filters</p>}
          </div>
        </div>
      )}
    </div>
  );
};

const DamagedStockReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(getTodayDate());
  const [toDate, setToDate] = useState(getTodayDate());
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('pos_token');
    axios.get(`${API}/products/enhanced`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setProducts(r.data || []))
      .catch(() => {});
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('pos_token');
      const params = { from_date: fromDate, to_date: toDate };
      if (selectedProduct) params.product_id = selectedProduct;
      const resp = await axios.get(`${API}/reports/damaged-stock`, {
        headers: { Authorization: `Bearer ${token}` }, params
      });
      setData(resp.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const exportPDF = async () => {
    if (!data?.data?.length) return;
    const metaLines = [
      `Period: ${fromDate} to ${toDate}`,
      `Total Records: ${data.summary.total_records} | Total Qty: ${data.summary.total_quantity} | Total Value: Rs.${data.summary.total_value.toFixed(2)}`
    ];
    const head = ['Sl', 'Date', 'Product', 'Qty', 'Reason', 'Notes', 'Value'];
    const tableBody = data.data.map((r, i) => [i + 1, r.date || '-', r.product_name, r.quantity, r.reason, r.notes || '-', (r.value || 0).toFixed(2)]);
    if (hasIndicScript(data.data.flatMap(r => [r.product_name, r.reason, r.notes]).filter(Boolean).join(' '))) {
      await exportTableReportPdf({
        title: 'Damaged Stock Report', metaLines, orientation: 'l',
        filename: `Damaged_Stock_${fromDate}_to_${toDate}.pdf`,
        sections: [{ head, body: tableBody, align: ['c', '', '', 'r', '', '', 'r'], headColor: '#dc2626' }]
      });
      return;
    }
    const pdf = new jsPDF('l', 'mm', 'a4');
    pdf.setFontSize(14); pdf.setFont('helvetica', 'bold');
    pdf.text('Damaged Stock Report', 14, 15);
    pdf.setFontSize(9); pdf.setFont('helvetica', 'normal');
    pdf.text(`Period: ${fromDate} to ${toDate}`, 14, 22);
    pdf.text(`Total Records: ${data.summary.total_records} | Total Qty: ${data.summary.total_quantity} | Total Value: Rs.${data.summary.total_value.toFixed(2)}`, 14, 28);
    autoTable(pdf, {
      startY: 33,
      head: [head],
      body: tableBody,
      theme: 'grid', headStyles: { fillColor: [220, 38, 38], fontSize: 8 }, bodyStyles: { fontSize: 7.5 },
      margin: { left: 14, right: 14 },
      didParseCell: autoTableMultilingualHook
    });
    pdf.save(`Damaged_Stock_${fromDate}_to_${toDate}.pdf`);
  };

  const exportCSV = () => {
    if (!data?.data?.length) return;
    const headers = ['Sl', 'Date', 'Product', 'Qty', 'Reason', 'Notes', 'Value'];
    const csvRows = [headers.join(',')];
    data.data.forEach((r, i) => csvRows.push([i+1, r.date, `"${r.product_name}"`, r.quantity, `"${r.reason}"`, `"${r.notes || ''}"`, (r.value||0).toFixed(2)].join(',')));
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `Damaged_Stock_${fromDate}_to_${toDate}.csv`; a.click();
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-500">From Date</label>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500">To Date</label>
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500">Product</label>
          <select value={selectedProduct} onChange={e => setSelectedProduct(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
            <option value="">All Products</option>
            {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div className="flex items-end">
          <button onClick={fetchReport} disabled={loading} className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm font-medium" data-testid="damaged-report-generate">
            {loading ? <RefreshCw size={16} className="animate-spin" /> : <Search size={16} />}
            Generate
          </button>
        </div>
      </div>

      {data && (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-red-50 p-3 rounded-lg"><p className="text-xs text-slate-500">Total Records</p><p className="text-lg font-bold text-red-700">{data.summary.total_records}</p></div>
            <div className="bg-red-50 p-3 rounded-lg"><p className="text-xs text-slate-500">Total Quantity</p><p className="text-lg font-bold text-red-700">{data.summary.total_quantity}</p></div>
            <div className="bg-red-50 p-3 rounded-lg"><p className="text-xs text-slate-500">Total Value</p><p className="text-lg font-bold text-red-700">Rs.{data.summary.total_value.toFixed(2)}</p></div>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={exportPDF} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700" data-testid="damaged-report-pdf"><Download size={14} /> PDF</button>
            <button onClick={exportCSV} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"><Download size={14} /> CSV</button>
          </div>
          <div className="overflow-x-auto bg-white rounded-xl border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Sl</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Date</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Product</th>
                  <th className="px-3 py-2 text-right font-medium text-slate-600">Qty</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Reason</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Notes</th>
                  <th className="px-3 py-2 text-right font-medium text-slate-600">Value</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((r, i) => (
                  <tr key={`row-${i}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2">{i+1}</td>
                    <td className="px-3 py-2 text-xs">{r.date || '-'}</td>
                    <td className="px-3 py-2 font-medium">{r.product_name}</td>
                    <td className="px-3 py-2 text-right text-red-600 font-medium">{r.quantity}</td>
                    <td className="px-3 py-2">{r.reason}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{r.notes || '-'}</td>
                    <td className="px-3 py-2 text-right font-medium text-red-600">Rs.{(r.value||0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.data.length === 0 && <p className="text-center py-8 text-slate-400">No damaged stock records found</p>}
          </div>
        </div>
      )}
    </div>
  );
};

const Reports = () => {
  const [activeTab, setActiveTab] = useState('sales');

  return (
    <div data-testid="reports-page" className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Reports</h1>
        <p className="text-slate-500">Generate detailed reports with filters</p>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="flex border-b border-slate-200">
          <TabButton
            active={activeTab === 'sales'}
            onClick={() => setActiveTab('sales')}
            icon={FileText}
            label="Sales Report"
          />
          <TabButton
            active={activeTab === 'stockin'}
            onClick={() => setActiveTab('stockin')}
            icon={TrendingUp}
            label="Stock In Report"
          />
          <TabButton
            active={activeTab === 'movement'}
            onClick={() => setActiveTab('movement')}
            icon={Package}
            label="Stock Movement"
          />
          <TabButton
            active={activeTab === 'gst'}
            onClick={() => setActiveTab('gst')}
            icon={Receipt}
            label="GST Reports"
          />
          <TabButton
            active={activeTab === 'product-sales'}
            onClick={() => setActiveTab('product-sales')}
            icon={ShoppingBag}
            label="Product Sales"
          />
          <TabButton
            active={activeTab === 'customer-sales'}
            onClick={() => setActiveTab('customer-sales')}
            icon={Users}
            label="Customer Sales"
          />
          <TabButton
            active={activeTab === 'damaged-stock'}
            onClick={() => setActiveTab('damaged-stock')}
            icon={TrendingDown}
            label="Damaged Stock"
          />
        </div>

        <div className="p-4">
          {activeTab === 'sales' && <SalesReport />}
          {activeTab === 'stockin' && <StockInReport />}
          {activeTab === 'movement' && <StockMovementReport />}
          {activeTab === 'gst' && <GSTReport />}
          {activeTab === 'product-sales' && <ProductWiseSalesReport />}
          {activeTab === 'customer-sales' && <CustomerWiseSalesReport />}
          {activeTab === 'damaged-stock' && <DamagedStockReport />}
        </div>
      </div>
    </div>
  );
};

export default Reports;
